/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE, API_TIMEOUT } from "utils";
import { getDatesWithoutWeekends, hitungtotaljamkerja } from "utils/func";

function RekapLaporanDinas() {
  const [loading, setLoading] = useState(false);
  const [dataRekapDinas, setDataRekapDinas] = useState(null);
  const [tanggalAwal, setTanggalAwal] = useState("");
  const [tanggalAkhir, setTanggalAkhir] = useState("");
  const [userId, setUserId] = useState(null);
  const [userDinasId, setUserDinasId] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [detailModalData, setDetailModalData] = useState({
    type: "",
    title: "",
    data: [],
    namaPegawai: "",
  });
  const [kinerjaData, setKinerjaData] = useState([]);
  const [apelRekapData, setApelRekapData] = useState(null);
  const [unitKerja, setUnitKerja] = useState("");
  const [listLibur, setListLibur] = useState([]);
  const [totalWorkingHoursIdeal, setTotalWorkingHoursIdeal] = useState("0:00");
  const [totalHariKerja, setTotalHariKerja] = useState(0);
  const navigate = useNavigate();

  const periodeLabel =
    tanggalAwal && tanggalAkhir
      ? `${new Date(tanggalAwal).toLocaleDateString("id-ID")} s/d ${new Date(tanggalAkhir).toLocaleDateString("id-ID")}`
      : "";

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const getLibur = useCallback(() => {
    axios({
      method: "get",
      url: "https://smartservice.rotendaokab.go.id/api/admin/libur",
      timeout: API_TIMEOUT,
      headers: { Authorization: `Bearer ${""}` },
    })
      .then((res) => {
        if (res.status === 200 && res.data && res.data.data) {
          setListLibur(res.data.data);
        }
      })
      .catch((err) => console.error("Error fetching libur:", err));
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUserId(user.uid_RSS || user.uid);
    setUserDinasId(user.dinas_id);
    setUnitKerja(user.nama_dinas || "");

    // Default: tanggal awal = 1 (bulan berjalan), tanggal akhir = tanggal hari ini
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    setTanggalAwal(`${year}-${String(month + 1).padStart(2, "0")}-01`);
    setTanggalAkhir(`${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
    getLibur();
  }, [getLibur]);

  // Fetch daftar hadir (SmartDH - sama seperti smartDH/kinerja)
  const fetchKinerjaData = useCallback(async () => {
    if (!unitKerja || !tanggalAwal || !tanggalAkhir) return;
    try {
      const res = await axios.post(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/instansi/reguler/update",
        { tAwal: tanggalAwal, tAkhir: tanggalAkhir, unitKerja },
        { headers: { "Content-Type": "application/json" }, timeout: API_TIMEOUT }
      );
      if (res.data && res.data.success === 1 && Array.isArray(res.data.data)) {
        setKinerjaData(res.data.data);
      } else {
        setKinerjaData([]);
      }
    } catch (err) {
      console.error("Error fetching kinerja/daftar hadir:", err);
      setKinerjaData([]);
    }
  }, [unitKerja, tanggalAwal, tanggalAkhir]);

  // Fetch rekap apel (untuk data apel pagi/siang per pegawai)
  const fetchApelRekap = useCallback(async () => {
    if (!userDinasId || !tanggalAwal || !tanggalAkhir) return;
    try {
      const res = await axios.get(
        `${API_ESA_OFFICE}apel-harian/dinas/${userDinasId}/rekap?tanggal_mulai=${tanggalAwal}&tanggal_akhir=${tanggalAkhir}`,
        { timeout: API_TIMEOUT }
      );
      if (res.data && res.data.data_pegawai) {
        setApelRekapData(res.data);
      } else {
        setApelRekapData(null);
      }
    } catch (err) {
      console.error("Error fetching apel rekap:", err);
      setApelRekapData(null);
    }
  }, [userDinasId, tanggalAwal, tanggalAkhir]);

  // Total hari kerja dan jam kerja ideal (untuk persentase)
  useEffect(() => {
    if (tanggalAwal && tanggalAkhir) {
      const dates = getDatesWithoutWeekends(
        tanggalAwal,
        tanggalAkhir,
        Array.isArray(listLibur) ? listLibur : []
      );
      setTotalHariKerja(dates.length);
      setTotalWorkingHoursIdeal(hitungtotaljamkerja(dates) || "0:00");
    }
  }, [listLibur, tanggalAwal, tanggalAkhir]);

  // Persentase capaian jam kerja (sumber SmartDH/kinerja)
  const totalWorkingMinutes = useMemo(() => {
    const wh = totalWorkingHoursIdeal;
    if (!wh || wh === "0:00") return 0;
    const [h, m] = String(wh).split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  }, [totalWorkingHoursIdeal]);

  const calculatePersentaseJamKerja = (workingHours) => {
    if (!workingHours || workingHours === "0:00" || totalWorkingMinutes === 0) return 0;
    const [h, m] = String(workingHours).split(":").map(Number);
    const minutes = (h || 0) * 60 + (m || 0);
    return Math.round((minutes / totalWorkingMinutes) * 10000) / 100;
  };

  const formatWorkingHours = (workingHours) => {
    if (!workingHours || workingHours === "0:00") return "0:00";
    return workingHours;
  };

  const calculateCorrectPercentage = (hadir, totalHari) => {
    if (!totalHari || totalHari === 0) return 0;
    return ((hadir || 0) / totalHari) * 100;
  };

  const fetchRekapDinas = () => {
    if (!userDinasId || !tanggalAwal || !tanggalAkhir) {
      swal("Error", "Data dinas atau tanggal tidak valid", "error");
      return;
    }

    setLoading(true);
    axios
      .get(
        `${API_ESA_OFFICE}laporan-harian/dinas/${userDinasId}/rekap?tanggal_mulai=${tanggalAwal}&tanggal_akhir=${tanggalAkhir}`
      )
      .then((res) => {
        setDataRekapDinas(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching rekap dinas:", err);
        setLoading(false);
        swal("Error", "Gagal mengambil data rekap dinas", "error");
      });
  };

  const handleTanggalChange = () => {
    if (tanggalAwal && tanggalAkhir) {
      fetchKinerjaData(); // endpoint SmartDH/daftar hadir
      fetchApelRekap();   // endpoint data apel rekap
      fetchRekapDinas(); // endpoint rekap laporan harian
    }
  };

  // Auto-fetch data ketika tanggal/dinas berubah: panggil endpoint SmartDH, apel rekap, dan rekap laporan harian
  useEffect(() => {
    if (!userDinasId || !tanggalAwal || !tanggalAkhir || loading) return;
    fetchKinerjaData();
    fetchApelRekap();
    fetchRekapDinas();
  }, [tanggalAwal, tanggalAkhir, userDinasId]);

  const handleDetailClick = (pegawai) => {
    navigate(
      `/daily-performance/laporan-harian/perorang-detail/${pegawai.uid_RSS}?tanggal_awal=${tanggalAwal}&tanggal_akhir=${tanggalAkhir}&nama=${pegawai.nama_pegawai}`
    );
  };

  const handleOpenDetailModal = (type, data, namaPegawai) => {
    let title = "";
    switch (type) {
      case "laporan_harian":
        title = `Detail Laporan Harian - ${namaPegawai} - ${periodeLabel}`;
        break;
      case "instruksi":
        title = `Detail Instruksi Selesai - ${namaPegawai} - ${periodeLabel}`;
        break;
      case "disposisi":
        title = `Detail Disposisi Selesai - ${namaPegawai} - ${periodeLabel}`;
        break;
      case "events":
        title = `Detail Event - ${namaPegawai} - ${periodeLabel}`;
        break;
      case "tamu":
        title = `Detail Tamu - ${namaPegawai} - ${periodeLabel}`;
        break;
      default:
        title = "Detail";
    }
    setDetailModalData({
      type,
      title,
      data: data || [],
      namaPegawai: namaPegawai || "",
    });
    setOpenDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setDetailModalData({
      type: "",
      title: "",
      data: [],
      namaPegawai: "",
    });
  };

  // Fungsi untuk export Excel (sama dengan data tabel, termasuk presensi & apel)
  const handleExportExcel = () => {
    if (!rows.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    const tableHeaders = [
      "No",
      "Nama Pegawai",
      "Jabatan",
      "Jam Kerja",
      "Hadir (Presensi)",
      "Tidak Hadir (Presensi)",
      "Apel Pagi Hadir",
      "Apel Pagi Total",
      "Apel Siang Hadir",
      "Apel Siang Total",
      "Total Kinerja (%)",
      "Jumlah Laporan",
      "Total Kegiatan",
      "Instruksi",
      "Disposisi",
      "Event",
      "Tamu",
      "Total Aktivitas",
    ];

    let tableRows = "";
    rows.forEach((r, index) => {
      const jmlLaporan = Array.isArray(r.laporan_harian_data) ? r.laporan_harian_data.length : 0;
      const totalKegiatan = Array.isArray(r.laporan_harian_data)
        ? r.laporan_harian_data.reduce((s, l) => s + (l.jumlah_kegiatan || 0), 0)
        : 0;
      const jmlInstruksi = Array.isArray(r.instruksi_data) ? r.instruksi_data.length : 0;
      const jmlDisposisi = Array.isArray(r.disposisi_data) ? r.disposisi_data.length : 0;
      const jmlEvents = Array.isArray(r.events_data) ? r.events_data.length : 0;
      const jmlTamu = Array.isArray(r.tamu_data) ? r.tamu_data.length : 0;
      const jamKerja = r.workingHours || "0:00";
      const hadirPresensi = r.hadirDaftarHadir ?? 0;
      const tidakHadirPresensi = r.tidakHadirDaftarHadir ?? 0;
      const apelPagiHadir = r.apel_pagi?.hadir ?? 0;
      const apelPagiTotal = totalHariKerja || 0;
      const apelSiangHadir = r.apel_siang?.hadir ?? 0;
      const apelSiangTotal = totalHariKerja || 0;
      const totalKinerjaPct = r.totalKinerja ?? 0;
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${r.nama_pegawai || ""}</td>
          <td>${r.nama_jabatan || ""}</td>
          <td>${jamKerja}</td>
          <td>${hadirPresensi}</td>
          <td>${tidakHadirPresensi}</td>
          <td>${apelPagiHadir}</td>
          <td>${apelPagiTotal}</td>
          <td>${apelSiangHadir}</td>
          <td>${apelSiangTotal}</td>
          <td>${totalKinerjaPct}%</td>
          <td>${jmlLaporan}</td>
          <td>${totalKegiatan}</td>
          <td>${jmlInstruksi}</td>
          <td>${jmlDisposisi}</td>
          <td>${jmlEvents}</td>
          <td>${jmlTamu}</td>
          <td>${r.total_activities ?? 0}</td>
        </tr>
      `;
    });

    const periode = dataRekapDinas?.periode || {};
    const st = statistikTabel;
    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Rekap Laporan Dinas - ${tanggalAwal} s/d ${tanggalAkhir}</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Rekap Laporan Dinas - ${dataRekapDinas?.nama_dinas || ""}</h2>
          <h3>Periode: ${new Date(tanggalAwal).toLocaleDateString("id-ID")} s/d ${new Date(tanggalAkhir).toLocaleDateString("id-ID")} (${periode.hari_kerja ?? 0} hari kerja)</h3>
          <h3>Statistik (sesuai data tabel):</h3>
          <ul>
            <li>Total Pegawai: ${st.total_pegawai}</li>
            <li>Sudah Lapor: ${st.sudah_lapor}</li>
            <li>Total Laporan: ${st.total_laporan}</li>
            <li>Total Instruksi: ${st.total_instruksi}</li>
            <li>Total Disposisi: ${st.total_disposisi}</li>
            <li>Total Event: ${st.total_events}</li>
            <li>Total Tamu: ${st.total_tamu}</li>
            <li>Total Aktivitas: ${st.total_aktivitas}</li>
            <li>Rata-rata Total Kinerja: ${st.rata_rata_total_kinerja}%</li>
            <li>Total Hadir (Presensi): ${st.total_hadir_presensi}</li>
            <li>Total Tidak Hadir (Presensi): ${st.total_tidak_hadir_presensi}</li>
            <li>Total Apel Pagi Hadir: ${st.total_apel_pagi_hadir}</li>
            <li>Total Apel Siang Hadir: ${st.total_apel_siang_hadir}</li>
          </ul>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(blob, `rekap-laporan-dinas-${tanggalAwal}-${tanggalAkhir}.xls`);
  };

  // Kolom sama seperti laporanDinas.js (akumulasi periode)
  const columns = [
    { Header: "No", accessor: "index" },
    {
      Header: "Profil",
      accessor: "profil",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
            alt={row.original.nama_pegawai}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 20 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    { Header: "Nama Pegawai", accessor: "nama_pegawai" },
    { Header: "Jabatan", accessor: "nama_jabatan" },
    {
      Header: "Presensi dan Apel",
      accessor: "presensi_apel",
      width: "16%",
      Cell: ({ row }) => {
        const r = row.original;
        const jamKerja = formatWorkingHours(r.workingHours);
        const persen = calculatePersentaseJamKerja(r.workingHours);
        const apelPagi = r.apel_pagi;
        const apelSiang = r.apel_siang;
        const hadirPagi = apelPagi?.hadir || 0;
        const hadirSiang = apelSiang?.hadir || 0;
        const persenPagi = Math.round(calculateCorrectPercentage(hadirPagi, totalHariKerja));
        const persenSiang = Math.round(calculateCorrectPercentage(hadirSiang, totalHariKerja));
        const hadir = r.hadirDaftarHadir ?? 0;
        const tidakHadir = r.tidakHadirDaftarHadir ?? 0;
        return (
          <MDBox>
            <MDTypography variant="caption" color="secondary" fontWeight="bold" display="block">
              Presensi (Daftar Hadir):
            </MDTypography>
            <MDTypography variant="caption" color="primary" display="block">
              Jam Kerja: {jamKerja || "-"}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Capaian: {persen}%
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Hadir: {hadir} | Tidak hadir: {tidakHadir}
            </MDTypography>
            <MDTypography variant="caption" color="secondary" fontWeight="bold" display="block" mt={0.5}>
              Apel Pagi:
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Hadir {hadirPagi}/{totalHariKerja || 0} ({persenPagi}%)
            </MDTypography>
            <MDTypography variant="caption" color="secondary" fontWeight="bold" display="block" mt={0.5}>
              Apel Siang:
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Hadir {hadirSiang}/{totalHariKerja || 0} ({persenSiang}%)
            </MDTypography>
          </MDBox>
        );
      },
    },
    {
      Header: "Laporan Harian & Aksi",
      accessor: "laporan_harian",
      width: "12%",
    },
    {
      Header: "Instruksi",
      accessor: "instruksi",
      width: "140px",
      minWidth: 140,
      maxWidth: 140,
    },
    {
      Header: "Disposisi",
      accessor: "disposisi",
      width: "140px",
      minWidth: 140,
      maxWidth: 140,
    },
    {
      Header: "Event",
      accessor: "events",
      width: "140px",
      minWidth: 140,
      maxWidth: 140,
    },
    {
      Header: "Tamu",
      accessor: "tamu",
      width: "140px",
      minWidth: 140,
      maxWidth: 140,
    },
    {
      Header: "Total Kinerja",
      accessor: "totalKinerja",
      width: "10%",
      Cell: ({ row }) => {
        const nilai = row.original.totalKinerja ?? 0;
        return (
          <MDBox>
            <Chip
              label={`${nilai}%`}
              color={
                nilai >= 80 ? "success" : nilai >= 60 ? "warning" : "error"
              }
              size="small"
              variant="filled"
            />
            <MDBox mt={0.5}>
              <MDProgress
                value={Math.min(100, nilai)}
                color={
                  nilai >= 80 ? "success" : nilai >= 60 ? "warning" : "error"
                }
                variant="gradient"
                sx={{ height: 6, borderRadius: 2 }}
              />
            </MDBox>
          </MDBox>
        );
      },
    },
    {
      Header: "Status",
      accessor: "total_activities",
      width: "8%",
      Cell: ({ row }) => {
        const pegawai = row.original;
        const total =
          (Array.isArray(pegawai.disposisi_data) ? pegawai.disposisi_data.length : 0) +
          (Array.isArray(pegawai.instruksi_data) ? pegawai.instruksi_data.length : 0) +
          (Array.isArray(pegawai.tamu_data) ? pegawai.tamu_data.length : 0) +
          (Array.isArray(pegawai.events_data) ? pegawai.events_data.length : 0) +
          (Array.isArray(pegawai.laporan_harian_data) ? pegawai.laporan_harian_data.length : 0);
        return (
          <MDBox>
            <Chip
              label={total}
              color={total > 0 ? "success" : "default"}
              size="small"
              variant="filled"
            />
            <MDButton
              variant="text"
              color="info"
              size="small"
              onClick={() => handleDetailClick(pegawai)}
              sx={{ mt: 0.5, minWidth: "auto", px: 0.5 }}
            >
              Detail
            </MDButton>
          </MDBox>
        );
      },
    },
  ];

  // Rows: struktur sama seperti laporanDinas, data akumulasi periode; gabung dengan SmartDH (kinerja) + data apel rekap
  const dataPegawai = dataRekapDinas?.data_pegawai || [];
  const rowsUnsorted = dataPegawai.map((pegawai, index) => {
    const kinerja = Array.isArray(kinerjaData)
      ? kinerjaData.find(
          (k) =>
            String(k.uid) === String(pegawai.uid_RSS) ||
            (k.nama && pegawai.nama_pegawai && k.nama.trim() === pegawai.nama_pegawai.trim())
        )
      : null;
    const apelPegawai = apelRekapData?.data_pegawai
      ? apelRekapData.data_pegawai.find(
          (a) =>
            String(a.uid_RSS) === String(pegawai.uid_RSS) ||
            (a.nama_pegawai && pegawai.nama_pegawai && a.nama_pegawai.trim() === pegawai.nama_pegawai.trim())
        )
      : null;
    const workingHours = kinerja?.workingHours || "0:00";
    const hadirDaftarHadir = kinerja?.hadir ?? 0; // total hadir dari endpoint SmartDH
    const tidakHadirDaftarHadir = Math.max(0, totalHariKerja - hadirDaftarHadir); // tidak hadir = hari kerja - hadir
    const apel_pagi = apelPegawai?.apel_pagi;
    const apel_siang = apelPegawai?.apel_siang;

    const disposisiArray = Array.isArray(pegawai.disposisi) ? pegawai.disposisi : [];
    const instruksiArray = Array.isArray(pegawai.instruksi) ? pegawai.instruksi : [];
    const tamuArray = Array.isArray(pegawai.tamu) ? pegawai.tamu : [];
    const eventsArray = Array.isArray(pegawai.events) ? pegawai.events : [];
    const laporanArray = Array.isArray(pegawai.laporan_harian) ? pegawai.laporan_harian : [];
    // Aktivitas: instruksi, penyelesaian disposisi, kehadiran event, total tamu, dan laporan harian
    const totalActivities =
      instruksiArray.length +
      disposisiArray.length +
      eventsArray.length +
      tamuArray.length +
      laporanArray.length;

    // Presensi: capaian jam kerja & kehadiran (bobot besar), apel pagi (bobot lebih kecil)
    const capaianJam = calculatePersentaseJamKerja(workingHours);
    const persenKehadiran = totalHariKerja ? calculateCorrectPercentage(hadirDaftarHadir, totalHariKerja) : 0;
    const persenApelPagi = totalHariKerja ? calculateCorrectPercentage(apel_pagi?.hadir || 0, totalHariKerja) : 0;
    // Rata-rata tertimbang: jam kerja 40%, kehadiran 40%, apel pagi 20% (apel pagi diperkecil)
    const avgPresensiApel = capaianJam * 0.4 + persenKehadiran * 0.4 + persenApelPagi * 0.2;

    // Skor aktivitas: instruksi + disposisi + event + tamu + laporan (normalisasi 0–100, 10 aktivitas = 100%)
    const skorAktivitas = Math.min(100, totalActivities * 10);

    // Total kinerja: 60% presensi + 40% aktivitas (bobot instruksi, disposisi, event, tamu dinaikkan)
    const totalKinerja = Math.round(0.6 * avgPresensiApel + 0.4 * skorAktivitas);

    return {
      index: index + 1,
      totalKinerja,
      uid_RSS: pegawai.uid_RSS,
      nama_pegawai: pegawai.nama_pegawai,
      nama_jabatan: pegawai.nama_jabatan,
      workingHours,
      hadirDaftarHadir,
      tidakHadirDaftarHadir,
      apel_pagi,
      apel_siang,
      disposisi_data: disposisiArray,
      instruksi_data: instruksiArray,
      tamu_data: tamuArray,
      events_data: eventsArray,
      laporan_harian_data: laporanArray,
      total_activities: totalActivities,
      laporan_harian: (
        <MDBox>
          {laporanArray.length > 0 ? (
            <MDBox>
              <Chip
                label={`${laporanArray.length} Laporan`}
                color="info"
                size="small"
                sx={{ mb: 0.5, cursor: "pointer" }}
                onClick={() =>
                  handleOpenDetailModal("laporan_harian", laporanArray, pegawai.nama_pegawai)
                }
              />
              <MDTypography variant="caption" color="text" display="block">
                {laporanArray.reduce((sum, l) => sum + (l.jumlah_kegiatan || 0), 0)} Kegiatan
              </MDTypography>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => handleDetailClick(pegawai)}
                sx={{ mt: 0.5, fontSize: "0.7rem" }}
              >
                Lihat
              </MDButton>
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">-</MDTypography>
          )}
        </MDBox>
      ),
      instruksi: (
        <MDBox sx={{ maxWidth: 140, wordBreak: "break-word", whiteSpace: "normal", overflowWrap: "break-word" }}>
          {instruksiArray.length === 0 ? (
            <MDTypography variant="caption" color="text.secondary">-</MDTypography>
          ) : (
            <MDBox>
              {instruksiArray.filter((i) => i.jenis === "terima").length > 0 && (
                <MDBox mb={0.5}>
                  <Chip
                    label={`Terima: ${instruksiArray.filter((i) => i.jenis === "terima").length}`}
                    color="success"
                    size="small"
                    sx={{ mb: 0.5, cursor: "pointer" }}
                    onClick={() =>
                      handleOpenDetailModal(
                        "instruksi",
                        instruksiArray.filter((i) => i.jenis === "terima"),
                        pegawai.nama_pegawai
                      )
                    }
                  />
                  <MDTypography variant="caption" color="text" display="block">
                    {instruksiArray.find((i) => i.jenis === "terima")?.perihal || "-"}
                  </MDTypography>
                </MDBox>
              )}
              {instruksiArray.filter((i) => i.jenis === "beri").length > 0 && (
                <MDBox>
                  <Chip
                    label={`Beri: ${instruksiArray.filter((i) => i.jenis === "beri").length}`}
                    color="info"
                    size="small"
                    sx={{ mb: 0.5, cursor: "pointer" }}
                    onClick={() =>
                      handleOpenDetailModal(
                        "instruksi",
                        instruksiArray.filter((i) => i.jenis === "beri"),
                        pegawai.nama_pegawai
                      )
                    }
                  />
                  <MDTypography variant="caption" color="text" display="block">
                    {instruksiArray.find((i) => i.jenis === "beri")?.perihal || "-"}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          )}
        </MDBox>
      ),
      disposisi: (
        <MDBox sx={{ maxWidth: 140, wordBreak: "break-word", whiteSpace: "normal", overflowWrap: "break-word" }}>
          {disposisiArray.length === 0 ? (
            <MDTypography variant="caption" color="text.secondary">-</MDTypography>
          ) : (
            <MDBox>
              {disposisiArray.filter((d) => d.jenis === "terima").length > 0 && (
                <MDBox mb={0.5}>
                  <Chip
                    label={`Terima: ${disposisiArray.filter((d) => d.jenis === "terima").length}`}
                    color="warning"
                    size="small"
                    sx={{ mb: 0.5, cursor: "pointer" }}
                    onClick={() =>
                      handleOpenDetailModal(
                        "disposisi",
                        disposisiArray.filter((d) => d.jenis === "terima"),
                        pegawai.nama_pegawai
                      )
                    }
                  />
                  <MDTypography variant="caption" color="text" display="block">
                    {(disposisiArray.find((d) => d.jenis === "terima")?.perihal ||
                      disposisiArray.find((d) => d.jenis === "terima")?.nomor_surat) || "-"}
                  </MDTypography>
                </MDBox>
              )}
              {disposisiArray.filter((d) => d.jenis === "beri").length > 0 && (
                <MDBox>
                  <Chip
                    label={`Beri: ${disposisiArray.filter((d) => d.jenis === "beri").length}`}
                    color="info"
                    size="small"
                    sx={{ mb: 0.5, cursor: "pointer" }}
                    onClick={() =>
                      handleOpenDetailModal(
                        "disposisi",
                        disposisiArray.filter((d) => d.jenis === "beri"),
                        pegawai.nama_pegawai
                      )
                    }
                  />
                  <MDTypography variant="caption" color="text" display="block">
                    {(disposisiArray.find((d) => d.jenis === "beri")?.perihal ||
                      disposisiArray.find((d) => d.jenis === "beri")?.nomor_surat) || "-"}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          )}
        </MDBox>
      ),
      events: (
        <MDBox sx={{ maxWidth: 140, wordBreak: "break-word", whiteSpace: "normal", overflowWrap: "break-word" }}>
          {eventsArray.length > 0 ? (
            <MDBox>
              <Chip
                label={`${eventsArray.length} Event`}
                color="primary"
                size="small"
                sx={{ mb: 0.5, cursor: "pointer" }}
                onClick={() =>
                  handleOpenDetailModal("events", eventsArray, pegawai.nama_pegawai)
                }
              />
              <MDTypography variant="caption" color="text" display="block">
                {eventsArray[0]?.nama_event || "-"}
              </MDTypography>
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">-</MDTypography>
          )}
        </MDBox>
      ),
      tamu: (
        <MDBox sx={{ maxWidth: 140, wordBreak: "break-word", whiteSpace: "normal", overflowWrap: "break-word" }}>
          {tamuArray.length > 0 ? (
            <MDBox>
              <Chip
                label={`${tamuArray.length} Tamu`}
                color="secondary"
                size="small"
                sx={{ mb: 0.5, cursor: "pointer" }}
                onClick={() =>
                  handleOpenDetailModal("tamu", tamuArray, pegawai.nama_pegawai)
                }
              />
              <MDTypography variant="caption" color="text" display="block">
                {tamuArray[0]?.nama_tamu || "-"}
              </MDTypography>
            </MDBox>
          ) : (
            <MDTypography variant="caption" color="text.secondary">-</MDTypography>
          )}
        </MDBox>
      ),
    };
  });

  // Urutkan dari total kinerja tertinggi ke terendah, lalu perbarui nomor urut
  const rows = [...rowsUnsorted]
    .sort((a, b) => (b.totalKinerja ?? 0) - (a.totalKinerja ?? 0))
    .map((row, i) => ({ ...row, index: i + 1 }));

  // Statistik dari data tabel (rows) agar merepresentasi data yang ditampilkan
  const statistikTabel = useMemo(() => {
    if (!rows.length) {
      return {
        total_pegawai: 0,
        sudah_lapor: 0,
        total_laporan: 0,
        total_instruksi: 0,
        total_disposisi: 0,
        total_events: 0,
        total_tamu: 0,
        total_aktivitas: 0,
        rata_rata_total_kinerja: 0,
        total_hadir_presensi: 0,
        total_tidak_hadir_presensi: 0,
        total_apel_pagi_hadir: 0,
        total_apel_siang_hadir: 0,
      };
    }
    let totalLaporan = 0;
    let totalInstruksi = 0;
    let totalDisposisi = 0;
    let totalEvents = 0;
    let totalTamu = 0;
    let totalAktivitas = 0;
    let totalKinerjaSum = 0;
    let totalHadirPresensi = 0;
    let totalTidakHadirPresensi = 0;
    let totalApelPagiHadir = 0;
    let totalApelSiangHadir = 0;
    let sudahLapor = 0;
    rows.forEach((r) => {
      const jmlLaporan = Array.isArray(r.laporan_harian_data) ? r.laporan_harian_data.length : 0;
      totalLaporan += jmlLaporan;
      if (jmlLaporan > 0) sudahLapor += 1;
      totalInstruksi += Array.isArray(r.instruksi_data) ? r.instruksi_data.length : 0;
      totalDisposisi += Array.isArray(r.disposisi_data) ? r.disposisi_data.length : 0;
      totalEvents += Array.isArray(r.events_data) ? r.events_data.length : 0;
      totalTamu += Array.isArray(r.tamu_data) ? r.tamu_data.length : 0;
      totalAktivitas += r.total_activities ?? 0;
      totalKinerjaSum += r.totalKinerja ?? 0;
      totalHadirPresensi += r.hadirDaftarHadir ?? 0;
      totalTidakHadirPresensi += r.tidakHadirDaftarHadir ?? 0;
      totalApelPagiHadir += r.apel_pagi?.hadir ?? 0;
      totalApelSiangHadir += r.apel_siang?.hadir ?? 0;
    });
    return {
      total_pegawai: rows.length,
      sudah_lapor: sudahLapor,
      total_laporan: totalLaporan,
      total_instruksi: totalInstruksi,
      total_disposisi: totalDisposisi,
      total_events: totalEvents,
      total_tamu: totalTamu,
      total_aktivitas: totalAktivitas,
      rata_rata_total_kinerja: rows.length ? Math.round(totalKinerjaSum / rows.length) : 0,
      total_hadir_presensi: totalHadirPresensi,
      total_tidak_hadir_presensi: totalTidakHadirPresensi,
      total_apel_pagi_hadir: totalApelPagiHadir,
      total_apel_siang_hadir: totalApelSiangHadir,
    };
  }, [rows]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          {/* Statistik Card dengan Filter Tanggal */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox mb={3}>
                  <MDBox
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    gap={2}
                  >
                    <MDTypography variant="h6" gutterBottom>
                      Rekap Laporan Harian Dinas
                    </MDTypography>
                    <MDBox
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap={2}
                      width={{ xs: "100%", md: "auto" }}
                    >
                      <MDInput
                        type="date"
                        label="Tanggal Awal"
                        value={tanggalAwal}
                        onChange={(e) => setTanggalAwal(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth={isMobile}
                      />
                      <MDInput
                        type="date"
                        label="Tanggal Akhir"
                        value={tanggalAkhir}
                        onChange={(e) => setTanggalAkhir(e.target.value)}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth={isMobile}
                      />
                    </MDBox>
                  </MDBox>
                </MDBox>

                {dataRekapDinas && (
                  <>
                    <MDTypography variant="button" color="text" gutterBottom>
                      {dataRekapDinas.nama_dinas}
                    </MDTypography>
                    <MDTypography variant="button" color="text" gutterBottom>
                      Periode:{" "}
                      {dataRekapDinas.periode?.tanggal_mulai
                        ? new Date(dataRekapDinas.periode.tanggal_mulai).toLocaleDateString("id-ID")
                        : ""}{" "}
                      s/d{" "}
                      {dataRekapDinas.periode?.tanggal_akhir
                        ? new Date(dataRekapDinas.periode.tanggal_akhir).toLocaleDateString("id-ID")
                        : ""}{" "}
                      ({dataRekapDinas.periode?.hari_kerja ?? 0} hari kerja)
                    </MDTypography>

                    <MDBox mt={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                      <Chip label={`Total Pegawai: ${statistikTabel.total_pegawai}`} color="info" size="medium" variant="filled" />
                      <Chip label={`Total Laporan: ${statistikTabel.total_laporan}`} color="primary" size="medium" variant="filled" />
                      <Chip label={`Instruksi: ${statistikTabel.total_instruksi}`} color="warning" size="medium" variant="filled" />
                      <Chip label={`Disposisi: ${statistikTabel.total_disposisi}`} color="secondary" size="medium" variant="filled" />
                      <Chip label={`Total Aktivitas: ${statistikTabel.total_aktivitas}`} color="default" size="medium" variant="filled" />
                      <Chip label={`Rata-rata Kinerja: ${statistikTabel.rata_rata_total_kinerja}%`} color="info" size="medium" variant="filled" />
                      <Chip label={`Hadir (Presensi): ${statistikTabel.total_hadir_presensi}`} color="success" size="medium" variant="filled" />
                      <Chip label={`Tidak Hadir (Presensi): ${statistikTabel.total_tidak_hadir_presensi}`} color="error" size="medium" variant="filled" />
                      <Chip label={`Apel Pagi Hadir: ${statistikTabel.total_apel_pagi_hadir}`} color="primary" size="medium" variant="filled" />
                      <Chip label={`Apel Siang Hadir: ${statistikTabel.total_apel_siang_hadir}`} color="secondary" size="medium" variant="filled" />
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Tabel Data Pegawai */}
          {dataRekapDinas && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text">
                        Data Pegawai:{" "}
                        {new Date(tanggalAwal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}{" "}
                        s/d{" "}
                        {new Date(tanggalAkhir).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total: {dataRekapDinas.data_pegawai.length} pegawai
                      </MDTypography>
                    </MDBox>
                    <MDButton
                      variant="outlined"
                      color="default"
                      onClick={handleExportExcel}
                      disabled={loading}
                    >
                      <Icon>download</Icon>
                      &nbsp;Export Excel
                    </MDButton>
                  </MDBox>
                  {isMobile ? (
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {loading && (
                        <MDTypography variant="button" color="text.secondary">
                          Memuat...
                        </MDTypography>
                      )}
                      {!loading &&
                        rows.map((row, index) => (
                          <Card key={`${row.uid_RSS || "row"}-${index}`}>
                            <CardContent>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={4}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <img
                                      src={`https://smartdh.rotendaokab.go.id/uploads/${row.uid_RSS}.jpg`}
                                      alt={row.nama_pegawai}
                                      style={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        border: "2px solid #e0e0e0",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display =
                                          "flex";
                                      }}
                                    />
                                    <MDBox
                                      display="none"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        backgroundColor: "grey.200",
                                        color: "grey.500",
                                      }}
                                    >
                                      <Icon sx={{ fontSize: 36 }}>person</Icon>
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={8}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                  >
                                    {row.nama_pegawai}
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {row.nama_jabatan}
                                  </MDTypography>
                                  <MDBox mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                                    <Chip label={`Laporan: ${(row.laporan_harian_data && row.laporan_harian_data.length) || 0}`} color="info" size="small" />
                                    <Chip label={`Instruksi: ${(row.instruksi_data && row.instruksi_data.length) || 0}`} color="success" size="small" />
                                    <Chip label={`Disposisi: ${(row.disposisi_data && row.disposisi_data.length) || 0}`} color="warning" size="small" />
                                    <Chip label={`Event: ${(row.events_data && row.events_data.length) || 0}`} color="primary" size="small" />
                                    <Chip label={`Tamu: ${(row.tamu_data && row.tamu_data.length) || 0}`} color="secondary" size="small" />
                                    <Chip label={`Total: ${row.total_activities || 0}`} color="default" size="small" />
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDButton
                                      variant="gradient"
                                      color="info"
                                      size="small"
                                      onClick={() => handleDetailClick(row)}
                                      sx={{ minWidth: "auto", px: 1 }}
                                    >
                                      <Icon sx={{ fontSize: 16 }}>visibility</Icon>
                                      &nbsp;Detail
                                    </MDButton>
                                  </MDBox>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}
                    </MDBox>
                  ) : (
                    <MDBox sx={{ "& table": { tableLayout: "auto" } }}>
                      <DataTable
                        table={{ columns, rows }}
                        isSorted={false}
                        entriesPerPage={false}
                        showTotalEntries={false}
                        noEndBorder
                        loading={loading}
                      />
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="body1" color="text">
                    Memuat data rekap dinas...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!dataRekapDinas &&
            !loading &&
            userDinasId &&
            tanggalAwal &&
            tanggalAkhir && (
              <Grid item xs={12}>
                <Card>
                  <MDBox p={3} textAlign="center">
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      py={4}
                    >
                      <Icon
                        sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                      >
                        event_busy
                      </Icon>
                      <MDTypography variant="h6" color="text.secondary">
                        Tidak ada data rekap laporan dinas
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary">
                        Coba pilih periode tanggal lain atau periksa kembali
                        dinas
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            )}

          </Grid>

        {/* Dialog Detail Modal (sama seperti laporanDinas) */}
        <Dialog
          open={openDetailModal}
          onClose={handleCloseDetailModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h5">{detailModalData.title}</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {!detailModalData.data || detailModalData.data.length === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Tidak ada data untuk ditampilkan
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  {detailModalData.type === "laporan_harian" && (
                    <MDBox>
                      {detailModalData.data.map((laporan, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography variant="h6" color="info" gutterBottom>
                              Laporan #{index + 1}
                            </MDTypography>
                            <MDTypography variant="caption" color="text.secondary">
                              Tanggal: {new Date(laporan.tanggal).toLocaleDateString("id-ID")}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Jumlah Kegiatan:</MDTypography>
                              <MDTypography variant="body2" color="text">{laporan.jumlah_kegiatan || 0}</MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>ID Laporan:</MDTypography>
                              <MDTypography variant="body2" color="text">{laporan.id_laporan}</MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                  {detailModalData.type === "instruksi" && (
                    <MDBox>
                      {detailModalData.data.map((instruksi, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography variant="h6" color="success" gutterBottom>Instruksi #{index + 1}</MDTypography>
                            <MDTypography variant="caption" color="text.secondary">
                              Selesai: {instruksi.waktu_selesai ? new Date(instruksi.waktu_selesai).toLocaleString("id-ID") : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Perihal:</MDTypography>
                              <MDTypography variant="body2" color="text">{instruksi.perihal || "-"}</MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Isi Instruksi:</MDTypography>
                              <MDTypography variant="body2" color="text">{instruksi.isi_instruksi || "-"}</MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Prioritas:</MDTypography>
                              <Chip label={instruksi.prioritas || "-"} color={instruksi.prioritas === "Tinggi" ? "error" : instruksi.prioritas === "Sedang" ? "warning" : "default"} size="small" />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Pemberi Perintah:</MDTypography>
                              <MDTypography variant="body2" color="text">{instruksi.pemberi_perintah || "-"}</MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                  {detailModalData.type === "disposisi" && (
                    <MDBox>
                      {detailModalData.data.map((disposisi, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography variant="h6" color="warning" gutterBottom>Disposisi #{index + 1}</MDTypography>
                            <MDTypography variant="caption" color="text.secondary">
                              Selesai: {disposisi.waktu_selesai ? new Date(disposisi.waktu_selesai).toLocaleString("id-ID") : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Perihal:</MDTypography>
                              <MDTypography variant="body2" color="text">{disposisi.perihal || "-"}</MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Nomor Surat:</MDTypography>
                              <MDTypography variant="body2" color="text">{disposisi.nomor_surat || "-"}</MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Pemberi Perintah:</MDTypography>
                              <MDTypography variant="body2" color="text">{disposisi.pemberi_perintah || "-"}</MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                  {detailModalData.type === "events" && (
                    <MDBox>
                      {detailModalData.data.map((event, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography variant="h6" color="primary" gutterBottom>Event #{index + 1}</MDTypography>
                            <MDTypography variant="caption" color="text.secondary">
                              Waktu: {event.waktu ? new Date(event.waktu).toLocaleString("id-ID") : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Nama Event:</MDTypography>
                              <MDTypography variant="body2" color="text">{event.nama_event || "-"}</MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Detail Kegiatan:</MDTypography>
                              <MDTypography variant="body2" color="text">{event.detail_kegiatan || "-"}</MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Tempat:</MDTypography>
                              <MDTypography variant="body2" color="text">{event.tempat || "-"}</MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                  {detailModalData.type === "tamu" && (
                    <MDBox>
                      {detailModalData.data.map((tamu, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography variant="h6" color="secondary" gutterBottom>Tamu #{index + 1}</MDTypography>
                            <MDTypography variant="caption" color="text.secondary">
                              Waktu: {tamu.waktu_kedatangan ? new Date(tamu.waktu_kedatangan).toLocaleString("id-ID") : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Nama Tamu:</MDTypography>
                              <MDTypography variant="body2" color="text">{tamu.nama_tamu || "-"}</MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Instansi:</MDTypography>
                              <MDTypography variant="body2" color="text">{tamu.nama_instansi || "-"}</MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography variant="subtitle2" color="text" gutterBottom>Keperluan:</MDTypography>
                              <MDTypography variant="body2" color="text">{tamu.keperluan || "-"}</MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                </>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDetailModal}>
              Tutup
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default RekapLaporanDinas;
